// ==================== SERVICIO DE ADMINISTRACIÓN ====================
// Maneja todas las peticiones relacionadas con la gestión de usuarios

import axios from 'axios';
import { API_BASE_URL } from '../config/apiConfig';

/**
 * Obtiene el token de autenticación del localStorage
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

/**
 * Manejo de errores centralizado
 */
const handleError = (error, defaultMessage) => {
  if (error.response) {
    // Error de respuesta del servidor
    if (error.response.status === 403) {
      throw new Error('No tienes permisos para realizar esta acción. Solo los Super Administradores pueden acceder.');
    }
    throw new Error(error.response.data.mensaje || error.response.data.error || defaultMessage);
  } else if (error.request) {
    // Error de red
    throw new Error('No se pudo conectar con el servidor. Verifica tu conexión a internet.');
  } else {
    // Error desconocido
    throw new Error(error.message || defaultMessage);
  }
};

/**
 * Obtiene la lista de usuarios con filtros y paginación
 * @param {Object} params - Parámetros de consulta
 * @returns {Promise<Object>} Lista de usuarios y paginación
 */
export const obtenerUsuarios = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.search) queryParams.append('search', params.search);
    if (params.rol) queryParams.append('rol', params.rol);
    if (params.activo !== undefined) queryParams.append('activo', params.activo);

    const response = await axios.get(
      `${API_BASE_URL}/admin/usuarios?${queryParams.toString()}`,
      { headers: getAuthHeaders() }
    );

    return response.data;
  } catch (error) {
    handleError(error, 'Error al obtener la lista de usuarios');
  }
};

/**
 * Obtiene estadísticas detalladas de un usuario
 * @param {String} id - ID del usuario
 * @returns {Promise<Object>} Usuario y sus estadísticas
 */
export const obtenerEstadisticasUsuario = async (id) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/admin/usuarios/${id}/estadisticas`,
      { headers: getAuthHeaders() }
    );

    return response.data;
  } catch (error) {
    handleError(error, 'Error al obtener estadísticas del usuario');
  }
};

/**
 * Actualiza los datos de un usuario
 * @param {String} id - ID del usuario
 * @param {Object} datos - Datos a actualizar
 * @returns {Promise<Object>} Usuario actualizado
 */
export const actualizarUsuario = async (id, datos) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/admin/usuarios/${id}`,
      datos,
      { headers: getAuthHeaders() }
    );

    return response.data;
  } catch (error) {
    handleError(error, 'Error al actualizar el usuario');
  }
};

/**
 * Elimina un usuario y todos sus datos asociados
 * @param {String} id - ID del usuario
 * @returns {Promise<Object>} Estadísticas de eliminación
 */
export const eliminarUsuario = async (id) => {
  try {
    const response = await axios.delete(
      `${API_BASE_URL}/admin/usuarios/${id}`,
      { headers: getAuthHeaders() }
    );

    return response.data;
  } catch (error) {
    handleError(error, 'Error al eliminar el usuario');
  }
};

/**
 * Activa o desactiva un usuario
 * @param {String} id - ID del usuario
 * @returns {Promise<Object>} Usuario con nuevo estado
 */
export const toggleActivoUsuario = async (id) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/admin/usuarios/${id}/toggle-active`,
      {},
      { headers: getAuthHeaders() }
    );

    return response.data;
  } catch (error) {
    handleError(error, 'Error al cambiar el estado del usuario');
  }
};

/**
 * Obtiene el historial de logs de auditoría
 * @param {Object} params - Parámetros de consulta
 * @returns {Promise<Object>} Logs y paginación
 */
export const obtenerLogs = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);

    const response = await axios.get(
      `${API_BASE_URL}/admin/logs?${queryParams.toString()}`,
      { headers: getAuthHeaders() }
    );

    return response.data;
  } catch (error) {
    handleError(error, 'Error al obtener los logs de auditoría');
  }
};

/**
 * Obtiene estadísticas generales del sistema
 * @returns {Promise<Object>} Estadísticas generales y por usuario
 */
export const obtenerEstadisticasGenerales = async () => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/admin/estadisticas`,
      { headers: getAuthHeaders() }
    );

    return response.data;
  } catch (error) {
    handleError(error, 'Error al obtener estadísticas generales');
  }
};

/**
 * Obtiene información del super admin actual (proyecto y miembros)
 * @returns {Promise<Object>} Información del super admin
 */
export const obtenerMiInformacion = async () => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/admin/mi-informacion`,
      { headers: getAuthHeaders() }
    );

    return response.data;
  } catch (error) {
    handleError(error, 'Error al obtener información del super admin');
  }
};

/**
 * Verifica si el usuario actual tiene permisos de superadmin
 * @returns {Boolean}
 */
export const esSuperAdmin = () => {
  try {
    const usuario = JSON.parse(localStorage.getItem('usuario'));
    return usuario && usuario.rol === 'superadmin';
  } catch (error) {
    return false;
  }
};

/**
 * Obtiene los destinatarios (propietario + miembros) de un proyecto
 * @param {String} proyectoId - ID del proyecto
 * @returns {Promise<Object>} Propietario y miembros del proyecto
 */
export const obtenerDestinatariosPorProyecto = async (proyectoId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/admin/proyectos/${proyectoId}/destinatarios`,
      { headers: getAuthHeaders() }
    );

    return response.data;
  } catch (error) {
    handleError(error, 'Error al obtener destinatarios del proyecto');
  }
};

/**
 * Envía correos personalizados a destinatarios seleccionados
 * @param {Object} datos - { destinatarios, asunto, mensaje }
 * @returns {Promise<Object>} Resultado del envío
 */
export const enviarCorreoPersonalizado = async (datos) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/admin/enviar-correo-personalizado`,
      datos,
      { headers: getAuthHeaders() }
    );

    return response.data;
  } catch (error) {
    handleError(error, 'Error al enviar correos');
  }
};

// ==================== PLANTILLAS DE CORREO ====================

/**
 * Obtiene todas las plantillas de correo guardadas
 */
export const obtenerPlantillasCorreo = async () => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/admin/plantillas-correo`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    handleError(error, 'Error al obtener plantillas de correo');
  }
};

/**
 * Crea una nueva plantilla de correo
 * @param {Object} datos - { nombre, asunto, mensaje }
 */
export const crearPlantillaCorreo = async (datos) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/admin/plantillas-correo`,
      datos,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    handleError(error, 'Error al crear plantilla de correo');
  }
};

/**
 * Actualiza una plantilla de correo existente
 * @param {string} id - ID de la plantilla
 * @param {Object} datos - { nombre, asunto, mensaje }
 */
export const actualizarPlantillaCorreo = async (id, datos) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/admin/plantillas-correo/${id}`,
      datos,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    handleError(error, 'Error al actualizar plantilla de correo');
  }
};

/**
 * Elimina una plantilla de correo
 * @param {string} id - ID de la plantilla
 */
export const eliminarPlantillaCorreo = async (id) => {
  try {
    const response = await axios.delete(
      `${API_BASE_URL}/admin/plantillas-correo/${id}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    handleError(error, 'Error al eliminar plantilla de correo');
  }
};

/**
 * Registra el uso de una plantilla de correo
 * @param {string} id - ID de la plantilla
 */
export const usarPlantillaCorreo = async (id) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/admin/plantillas-correo/${id}/usar`,
      {},
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    handleError(error, 'Error al registrar uso de plantilla');
  }
};

/**
 * Actualiza las apps visibles de un proyecto
 * @param {string} proyectoId - ID del proyecto
 * @param {Object} appsVisibles - { gestracoo: bool, recepcion: bool }
 */
export const actualizarAppsVisibles = async (proyectoId, appsVisibles) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/admin/proyectos/${proyectoId}/apps-visibles`,
      { appsVisibles },
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    handleError(error, 'Error al actualizar apps visibles');
  }
};

const adminService = {
  obtenerUsuarios,
  obtenerEstadisticasUsuario,
  actualizarUsuario,
  eliminarUsuario,
  toggleActivoUsuario,
  obtenerLogs,
  obtenerEstadisticasGenerales,
  obtenerMiInformacion,
  esSuperAdmin,
  obtenerDestinatariosPorProyecto,
  enviarCorreoPersonalizado,
  obtenerPlantillasCorreo,
  crearPlantillaCorreo,
  actualizarPlantillaCorreo,
  eliminarPlantillaCorreo,
  usarPlantillaCorreo,
  actualizarAppsVisibles
};

export default adminService;
